import React, { useReducer, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import JobBrowsing from './JobBrowsing';
import JobApplications from './jobApplications/view/JobApplications';

type State = {
  activeTab: 'browse' | 'applications';
};

type Action = 
  | { type: 'SET_ACTIVE_TAB'; payload: 'browse' | 'applications' }
  | { type: 'SYNC_WITH_LOCATION_STATE'; payload: any };

const initialState: State = {
  activeTab: 'browse'
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_ACTIVE_TAB':
      return { ...state, activeTab: action.payload };
      
    case 'SYNC_WITH_LOCATION_STATE':
      if (action.payload?.activeTab) {
        return { ...state, activeTab: action.payload.activeTab };
      }
      return state;
      
    default:
      return state;
  }
};

const CandidateJobManagement: React.FC = () => {
  const location = useLocation();
  const [state, dispatch] = useReducer(reducer, initialState, () => {
    // Initialize with location state
    const initialTab = location.state?.activeTab || 'browse';
    return { activeTab: initialTab };
  });

  // Sync with location state when it changes
  useEffect(() => {
    if (location.state?.activeTab && location.state.activeTab !== state.activeTab) {
      dispatch({ 
        type: 'SYNC_WITH_LOCATION_STATE', 
        payload: location.state 
      });
    }
  }, [location.state]);

  const handleTabChange = (tab: 'browse' | 'applications') => {
    dispatch({ type: 'SET_ACTIVE_TAB', payload: tab });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Jobs</h1>
            <p className="mt-2 text-gray-600">
              Browse and apply to job opportunities, track your applications
            </p>
          </div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => handleTabChange('browse')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                state.activeTab === 'browse'
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <span className="text-lg">🔍</span>
                <div className="text-left">
                  <div className="flex items-center space-x-2">
                    <span>Browse Jobs</span>
                  </div>
                  <div className="text-xs text-gray-500 font-normal">
                    Discover new opportunities
                  </div>
                </div>
              </div>
            </button>
            
            <button
              onClick={() => handleTabChange('applications')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                state.activeTab === 'applications'
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <span className="text-lg">📋</span>
                <div className="text-left">
                  <div className="flex items-center space-x-2">
                    <span>My Applications</span>
                  </div>
                  <div className="text-xs text-gray-500 font-normal">
                    Track application status
                  </div>
                </div>
              </div>
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {state.activeTab === 'browse' && <JobBrowsing />}
      {state.activeTab === 'applications' && <JobApplications />}
    </div>
  );
};

export default CandidateJobManagement;